import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { patternsService } from "../../services/patterns";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import SampleVideoRow from "../../components/patterns/SampleVideoRow";
import {
  formatNumber as defaultFormatNumber,
  formatRatioCompact,
  getReachLabel,
  getCreatorImpactLabel,
  getOpportunityLabelTint,
  LABEL_TINT_CLASSES,
} from "../../components/patterns/utils";

const PatternOverview = ({
  pattern,
  baseline,
  meta,
  audience,
  patternRequestId,
  formatNumber,
  patternVideoRows,
  loadingVideos,
  baseUrl,
  showExamples = true,
  defaultExpanded = false,
}) => {
  const location = useLocation();
  const formatNumberFn = formatNumber ?? defaultFormatNumber;
  const [successAnalysis, setSuccessAnalysis] = useState(null);
  const [loadingSuccessAnalysis, setLoadingSuccessAnalysis] = useState(false);
  const [successAnalysisError, setSuccessAnalysisError] = useState(null);

  const [opportunityExpanded, setOpportunityExpanded] = useState(defaultExpanded);
  const [analysisExpanded, setAnalysisExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (!pattern || patternRequestId == null) return;
    setSuccessAnalysis(null);
    setSuccessAnalysisError(null);
    setOpportunityExpanded(defaultExpanded);
    setAnalysisExpanded(defaultExpanded);
    setLoadingSuccessAnalysis(true);
    patternsService.analyzePatternAnalysis(
      { pattern_request_id: patternRequestId, slug: pattern.pattern_key ?? pattern.id ?? "" },
      (response) => {
        const data = response?.data;
        if (data && (data.description != null || data.how_it_performed != null || data.why != null)) {
          setSuccessAnalysis({
            description: data.description ?? "",
            howItPerformed: data.how_it_performed ?? "",
            why: data.why ?? [],
            bestUseCases: data.best_use_cases ?? [],
            opportunityLabel: data.opportunity_label ?? "Established",
            opportunityDescription: data.opportunity_description ?? "",
          });
        } else {
          setSuccessAnalysis(null);
        }
        setLoadingSuccessAnalysis(false);
      },
      () => {
        setSuccessAnalysisError("Could not load analysis.");
        setLoadingSuccessAnalysis(false);
      }
    );
  }, [pattern, patternRequestId, defaultExpanded]);

  const sortedVideoRows = useMemo(() => {
    const sorted = [...patternVideoRows];
    const score = (row, key) => {
      const v = Number(row?.[key]);
      return Number.isNaN(v) ? -Infinity : v;
    };
    sorted.sort((a, b) => {
      const likesDelta = score(b, "creator_likes_outlier") - score(a, "creator_likes_outlier");
      if (likesDelta !== 0) return likesDelta;
      return score(b, "creator_views_outlier") - score(a, "creator_views_outlier");
    });
    return sorted;
  }, [patternVideoRows]);

  const topVideoRows = sortedVideoRows.slice(0, 3);

  // Reach: median views, % vs niche median
  const medianViews = pattern?.stats?.views;
  const viewsBaselineRatio = pattern?.baseline_comparison?.views;

  // Creator Impact: composite outlier
  const compositeOutlier = pattern?.creator_comparison?.composite_outlier;

  // Usage for Opportunity card
  const videoCount = pattern?.stats?.video_count ?? 0;
  const totalCount = meta?.video_count ?? 0;
  const usagePct = totalCount > 0 ? (videoCount / totalCount) * 100 : null;

  const OPPORTUNITY_EMOJI = {
    "High Opportunity": "🔥",
    "Possible Gem": "💎",
    Rising: "📈",
    Established: "✓",
    Saturated: "⚠",
    Declining: "📉",
  };

  const reachLabel = getReachLabel(viewsBaselineRatio);
  const creatorImpactLabel = getCreatorImpactLabel(compositeOutlier);
  const opportunityTint = getOpportunityLabelTint(successAnalysis?.opportunityLabel);
  const verdictCardClass =
    "bg-theme-secondary rounded-lg border border-theme-secondary p-4 flex flex-col min-h-[100px]";

  return (
    <div className="space-y-8">
      {/* SECTION 1: Verdict cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Reach */}
        <div>
          <h4 className="text-sm font-semibold text-theme-text mb-3">Reach</h4>
          <div className={verdictCardClass}>
            {reachLabel && (
              <div
                className={`text-base font-bold flex items-center gap-1.5 mb-1 ${LABEL_TINT_CLASSES[reachLabel.tint] ?? "text-theme-text"}`}
              >
                <span>{reachLabel.emoji}</span>
                {reachLabel.label}
              </div>
            )}
            <div className="flex items-baseline gap-2 flex-1 min-h-[2.5rem]">
              <span className="text-2xl font-bold text-theme-text flex items-baseline gap-1">
                {medianViews != null ? (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5 opacity-40 translate-y-[2px]"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {formatNumberFn(medianViews)}
                  </>
                ) : (
                  "—"
                )}
              </span>
              <span className="text-sm font-medium text-theme-text opacity-80 whitespace-nowrap overflow-hidden text-ellipsis">
                ({formatRatioCompact(viewsBaselineRatio)?.text ?? "—"} audience avg)
              </span>
            </div>
          </div>
        </div>

        {/* Creator Impact */}
        <div>
          <div className="flex items-center gap-1.5 mb-3">
            <h4 className="text-sm font-semibold text-theme-text">Creator Impact</h4>
            <span
              className="inline-flex cursor-help text-theme-text opacity-60"
              data-tooltip-id="creator-impact-verdict-tip"
              data-tooltip-content="Performance score based on views, likes, and comments compared to other videos from these creators. This helps normalize the impact of different creator following sizes."
              aria-label="Creator impact explanation"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-3.5 h-3.5"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          </div>
          <div className={verdictCardClass}>
            {creatorImpactLabel && (
              <div
                className={`text-base font-bold flex items-center gap-1.5 mb-1 ${LABEL_TINT_CLASSES[creatorImpactLabel.tint] ?? "text-theme-text"}`}
              >
                <span>{creatorImpactLabel.emoji}</span>
                {creatorImpactLabel.label}
              </div>
            )}
            <div className="flex items-baseline gap-2 flex-1 min-h-[2.5rem]">
              <span className="text-2xl font-bold text-theme-text">
                {formatRatioCompact(compositeOutlier)?.text ?? "—"}
              </span>
              <span className="text-sm text-theme-text opacity-80">vs typical creator video</span>
            </div>
          </div>
        </div>

        {/* Opportunity */}
        <div>
          <h4 className="text-sm font-semibold text-theme-text mb-3">Opportunity</h4>
          <div className={verdictCardClass}>
            {loadingSuccessAnalysis ? (
              <div className="flex items-center justify-center">
                <OrbitalLoader special={true} size={60} />
                {/* <span className="text-sm">Analyzing...</span> */}
              </div>
            ) : successAnalysis?.opportunityLabel ? (
              <>
                <div
                  className={`text-base font-bold flex items-center gap-1.5 mb-0 ${LABEL_TINT_CLASSES[opportunityTint] ?? "text-theme-text"}`}
                >
                  <span>{OPPORTUNITY_EMOJI[successAnalysis.opportunityLabel] ?? "✓"}</span>
                  {successAnalysis.opportunityLabel}
                </div>
                <div
                  className={`text-sm text-theme-text opacity-80 leading-relaxed cursor-pointer hover:opacity-100 transition-opacity flex-1 min-h-[2.5rem] ${
                    !opportunityExpanded ? "line-clamp-2" : ""
                  }`}
                  onClick={() => setOpportunityExpanded((e) => !e)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(ev) => ev.key === "Enter" && setOpportunityExpanded((e) => !e)}
                  aria-expanded={opportunityExpanded}
                >
                  {successAnalysis.opportunityDescription ||
                    (usagePct != null && videoCount > 0
                      ? `${videoCount} videos / ${usagePct.toFixed(1)}% of audience`
                      : "")}
                </div>
              </>
            ) : (
              <div className="text-theme-text opacity-60">
                {usagePct != null && videoCount > 0 ? (
                  <>
                    <span className="font-semibold">{videoCount} videos</span>
                    <span className="ml-1">/ {usagePct.toFixed(1)}%</span>
                  </>
                ) : (
                  "—"
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Tooltip id="creator-impact-verdict-tip" place="top" className="!max-w-[260px] !text-xs" />

      {/* SECTION 2: Analysis */}
      <div>
        <h4 className="text-sm font-semibold text-theme-text mb-3">Expert Analysis</h4>
        <div
          className={`bg-theme-secondary rounded-lg border border-theme-secondary overflow-hidden relative ${
            !analysisExpanded ? "cursor-pointer" : ""
          }`}
          onClick={() => !loadingSuccessAnalysis && (successAnalysis || successAnalysisError) && setAnalysisExpanded((e) => !e)}
          role="button"
          tabIndex={!loadingSuccessAnalysis && (successAnalysis || successAnalysisError) ? 0 : undefined}
          onKeyDown={(ev) =>
            !loadingSuccessAnalysis &&
            (successAnalysis || successAnalysisError) &&
            ev.key === "Enter" &&
            setAnalysisExpanded((e) => !e)
          }
          aria-expanded={analysisExpanded}
        >
          <div
            className={`transition-all ${!analysisExpanded ? "h-[120px] overflow-hidden" : ""}`}
            style={!analysisExpanded ? { minHeight: 120 } : {}}
          >
            {loadingSuccessAnalysis ? (
              <div className="h-[120px] flex items-center justify-center">
                <OrbitalLoader special size={60} text="Analyzing pattern performance..." />
              </div>
            ) : successAnalysisError ? (
              <p className="text-sm text-theme-text opacity-70 p-4">{successAnalysisError}</p>
            ) : successAnalysis ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
                {/* Definition */}
                <div>
                  <h5 className="text-xs font-semibold text-theme-text opacity-70 uppercase tracking-wider mb-1.5">
                    Definition
                  </h5>
                  <p className="text-sm text-theme-text leading-relaxed">
                    {successAnalysis.description || "No definition available."}
                  </p>
                </div>

                {/* Why It Works */}
                <div>
                  <h5 className="text-xs font-semibold text-theme-text opacity-70 uppercase tracking-wider mb-1.5">
                    Why it works/doesn't
                  </h5>
                  {Array.isArray(successAnalysis.why) && successAnalysis.why.length > 0 ? (
                    <ul className="text-sm text-theme-text leading-relaxed space-y-1 list-none">
                      {successAnalysis.why.map((item, idx) => (
                        <li key={idx} className={item.direction === "negative" ? "text-red-500 dark:text-red-400" : ""}>
                          <span className="mr-1.5">{item.emoji}</span>
                          {item.reason}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-theme-text opacity-60">No analysis available.</p>
                  )}
                </div>

                {/* Best Use Cases */}
                <div>
                  <h5 className="text-xs font-semibold text-theme-text opacity-70 uppercase tracking-wider mb-1.5">
                    Best use cases
                  </h5>
                  {Array.isArray(successAnalysis.bestUseCases) && successAnalysis.bestUseCases.length > 0 ? (
                    <ul className="text-sm text-theme-text leading-relaxed space-y-1 list-disc list-inside">
                      {successAnalysis.bestUseCases.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-theme-text opacity-60">No use cases identified.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-theme-text opacity-60">No analysis available.</p>
            )}
          </div>
          {!analysisExpanded && (successAnalysis || successAnalysisError) && (
            <div
              className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
              style={{
                background: "linear-gradient(to top, var(--theme-secondary-bg) 0%, transparent 100%)",
              }}
            />
          )}
        </div>
      </div>

      {/* SECTION 3: Examples */}
      {showExamples ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-theme-text">Top Examples</h4>
            {sortedVideoRows.length > 3 && (
              <Link to={`${baseUrl}examples${location.search || ""}`} className="text-sm text-theme-primary hover:opacity-80">
                View all {sortedVideoRows.length} examples →
              </Link>
            )}
          </div>
          {loadingVideos ? (
            <div className="flex justify-center py-10 text-theme-text">
              <OrbitalLoader size={62} text="Loading videos..." />
            </div>
          ) : topVideoRows.length === 0 ? (
            <div className="py-8 text-center text-theme-text opacity-70 rounded-lg border border-theme-secondary bg-theme-main">
              No example videos for this pattern.
            </div>
          ) : (
            <div className="space-y-4">
              {topVideoRows.map((row) => (
                <SampleVideoRow
                  key={row.video?.id ?? row.hash}
                  video={row.video}
                  creator={row.creator}
                  creatorViewsOutlier={row.creator_views_outlier}
                  creatorLikesOutlier={row.creator_likes_outlier}
                  creatorCommentsOutlier={row.creator_comments_outlier}
                  content={row.content ?? {}}
                  formatNumber={formatNumberFn}
                  videoDetailUrl={
                    row.hash && audience?.hash ? `/audience/${audience.hash}/videos/${row.hash}/` : null
                  }
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PatternOverview;
